import { isEmpty, isNil, sortBy } from 'lodash'
import type { Root } from './rootModel'
import type { Address } from './addressModels'
import type { MemberPrivileges } from './memberModel'
import type { Identifier, IdentifierType } from './debtorModels'
import type { Currency } from './moneyModel'
import type { Country } from './countryModels'
import type { InvoiceOwner } from './invoiceModels'

export const paymentRails = ['SCT', 'SCTINST', 'FASTER_PAYMENTS', 'SWIFT'] as const
export type PaymentRail = (typeof paymentRails)[number]

export const guarantorOptions = ['aria', 'company'] as const
export type Guarantor = (typeof guarantorOptions)[number]

export type RegistrationNumber = {
    id: string
    type: IdentifierType
    value: string
    ownerId: string
    country?: Country['code']
    companyId?: string
}

export interface ContractInfos {
    templateId: string
    textTabsLabels: string[]
    roleName: string
}

export type PaymentDestination = {
    type: 'BANK_ACCOUNT'
    accountNumberType: 'IBAN' | 'ACCOUNT_NUMBER'
    accountNumber: string
    routingNumberType?: 'BIC' | 'SORT_CODE'
    routingNumber?: string
}

export type PaymentDestinationForm = {
    type: PaymentDestination['type'] | null
    accountNumberType: PaymentDestination['accountNumberType'] | null
    accountNumber: string
    routingNumberType: PaymentDestination['routingNumberType'] | null
    routingNumber: string
}

export const paymentTermOptionsList = ['net', 'eom', 'eomf'] as const
export type PaymentTermOption = (typeof paymentTermOptionsList)[number]

export function isPaymentTermOption(key: string): key is PaymentTermOption {
    return key === 'net' || key === 'eom' || key === 'eomf'
}

export type PaymentTermOptionType = {
    enabled: boolean
    min?: number | null
    max?: number | null
    enum?: number[]
}

export interface CompanyPerCurrencyConfig {
    currency: Currency
    repaymentDestinations: PaymentDestination[]
    overrideLoanPaymentDestination?: PaymentDestination
    overrideLoanPayoutLabel?: string
    availablePaymentTermOptions?: PaymentTermOption[]
    paymentTermOptions?: Partial<Record<PaymentTermOption, PaymentTermOptionType | null>>
    defaultGuarantor?: Guarantor
    defaultInvoiceOwner?: InvoiceOwner
    defaultInvoiceIssuer?: 'beneficiary' | 'company'
    availablePaymentRails?: PaymentRail[]
    defaultPaymentRail?: PaymentRail
}

export interface TimeInterval {
    value: number
    label: string
}

export interface CompanyConfigAttributes {
    advanceDurations?: number[]
    isCompanyClient: boolean
    hasContractInfos: boolean
    contractInfos?: ContractInfos
    overrideIBAN?: string
    overrideLabel?: string
    currencyConfigs: CompanyPerCurrencyConfig[]
    signerMemberIds?: string[]
    isGuarantorEnabled?: boolean
    needsKyc?: boolean
    areQuotesAvailable: boolean
    creditMemo: {
        enabled: boolean
    }
    receivable: {
        enabled: boolean
        maxInvoiceDelayInDays: number
    }
    repayments: {
        enabled: boolean
    }
}

export type Company = Root & {
    name: string
    config: CompanyConfigAttributes
    refundIBAN?: string
    refundBIC?: string
    ibanSwan?: string
    logo?: string
    address?: Address
    identifier: Identifier
}

export interface UpdateCompany {
    privileges?: MemberPrivileges
    name: Company['name']
    identifier: Identifier
    address: Pick<Address, 'first' | 'postal' | 'city' | 'country'>
}

export interface UpdateCompanyGateway {
    privileges?: MemberPrivileges
    name: Company['name']
    identifier: Identifier
    address: Pick<Address, 'first' | 'postal' | 'city' | 'country'>
}

export function sanitizePeriodTermsOption(paymentTermOptionType: PaymentTermOptionType | null | undefined): number[] {
    if (isNil(paymentTermOptionType) || !paymentTermOptionType.enabled) return []

    if (paymentTermOptionType.enum && paymentTermOptionType.enum.length > 0) return sortBy(paymentTermOptionType.enum)

    if (paymentTermOptionType.min && paymentTermOptionType.max)
        return sortBy([paymentTermOptionType.min, paymentTermOptionType.max])

    return []
}

export function mapCurrencyConfigs(
    currencyConfigs: CompanyPerCurrencyConfig[] | undefined
): CompanyPerCurrencyConfig[] {
    currencyConfigs?.forEach((currencyConfig) => {
        if (currencyConfig.paymentTermOptions) {
            const optionsList: PaymentTermOption[] = []
            Object.keys(currencyConfig.paymentTermOptions).forEach((option) => {
                // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the value comes from the option list this field offers, as it always has
                const optionKey = option as PaymentTermOption
                const termOption = currencyConfig.paymentTermOptions?.[optionKey]

                if (termOption) {
                    termOption.enum = sanitizePeriodTermsOption(termOption)
                    if (!isEmpty(termOption.enum)) optionsList.push(optionKey)
                }
            })
            currencyConfig.availablePaymentTermOptions = optionsList
        }
    })

    return currencyConfigs ?? []
}

/** The company with the payment term options it offers, sorted. */
export const toCompany = (company: Company): Company => ({
    ...company,
    config: {
        ...company.config,
        currencyConfigs: mapCurrencyConfigs(company.config.currencyConfigs)
    }
})
